'use client';

import type { ReactElement } from 'react';
import { Fragment, useState } from 'react';
import { Controller, type Control } from 'react-hook-form';
import { FormField } from '@/components/inventory/inventory-form-card/shared/FormField';
import { Button } from '@/components/ui/button';
import { AddressSearch } from '@/components/shared/address-search';
import type { StructuredAddress } from '@/components/shared/address-search';
import type { Contact } from '@/lib/services/contacts/types';
import type { ContactFormSectionProps } from './types';
import { useFieldChanges } from '@/components/documents/ai-import/review-card/field-changes-context';

export function AddressSection({
  control,
  mode,
  values,
  clickToCopy = false
}: ContactFormSectionProps): ReactElement {
  const fieldChanges = useFieldChanges();

  const hasAddressData = !!(
    values?.address?.streetAddress ||
    values?.address?.suburb ||
    values?.address?.state ||
    values?.address?.postcode ||
    values?.address?.country
  );
  const [showAddress, setShowAddress] = useState<boolean>(hasAddressData);

  if (mode === 'read') {
    if (fieldChanges?.address?.changeType === 'conflict') {
      return <Fragment />;
    }
    if (!hasAddressData) {
      return <Fragment />;
    }

    const addressParts: string[] = [];
    if (values.address?.streetAddress) {
      addressParts.push(values.address.streetAddress);
    }
    const cityStatePostcode = [
      values.address?.suburb,
      values.address?.state,
      values.address?.postcode
    ]
      .filter(Boolean)
      .join(' ');
    if (cityStatePostcode) {
      addressParts.push(cityStatePostcode);
    }
    if (values.address?.country) {
      addressParts.push(values.address.country);
    }

    const fullAddress = addressParts.join(', ');
    const addressChange = fieldChanges?.address;

    return (
      <Fragment>
        {fullAddress && (
          <FormField
            label="Address"
            value={fullAddress}
            mode="read"
            clickToCopy={clickToCopy}
            changeType={
              addressChange?.changeType === 'enrichment'
                ? 'enrichment'
                : undefined
            }
          />
        )}
      </Fragment>
    );
  }

  // Edit mode
  if (!showAddress) {
    return (
      <Button
        size="xs"
        density="compact"
        width="full"
        align="start"
        type="button"
        variant="ghost"
        onClick={() => setShowAddress(true)}
      >
        + Add Address
      </Button>
    );
  }

  return (
    <Controller
      control={control as Control<Partial<Contact>>}
      name="address"
      render={({ field, fieldState }) => {
        const addr = field.value as Contact['address'];
        const value: StructuredAddress = {
          streetAddress: addr?.streetAddress ?? '',
          suburb: addr?.suburb ?? '',
          state: addr?.state ?? '',
          postcode: addr?.postcode ?? '',
          country: addr?.country ?? ''
        };

        // Write back the five discrete fields the contact `address` shape
        // stores, preserving any sibling keys (businessName / poBox) and
        // dropping the provider's UI-only `formattedAddress`.
        const handleChange = (next: StructuredAddress): void => {
          field.onChange({
            ...addr,
            streetAddress: next.streetAddress,
            suburb: next.suburb,
            state: next.state,
            postcode: next.postcode,
            country: next.country
          });
        };

        return (
          <AddressSearch
            value={value}
            onChange={handleChange}
            error={fieldState.error?.message}
          />
        );
      }}
    />
  );
}
